import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, CreditCard, AlertTriangle } from 'lucide-react';
import { SubscriptionPlan } from '@/types/subscriptionPlans';
import { PlanType } from '@/hooks/useAdminPlans';
import { useStripePayment } from '@/hooks/stripe/useStripePayment';
import PaymentForm from './checkout/PaymentForm';
import OrderSummary from './checkout/OrderSummary';

interface SubscriptionCheckoutProps {
  selectedPlan: SubscriptionPlan | null;
  selectedPlanType: PlanType;
  isProcessing: boolean;
  onBackToPlanSelection: () => void;
  onProcessPayment: (formData: any) => Promise<void>;
}

const SubscriptionCheckout: React.FC<SubscriptionCheckoutProps> = ({
  selectedPlan,
  selectedPlanType,
  isProcessing,
  onBackToPlanSelection,
  onProcessPayment
}) => {
  const [cardNumber, setCardNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const { processPayment } = useStripePayment();

  const getPrice = () => {
    switch (selectedPlanType) {
      case 'monthly':
        return selectedPlan.monthlyPrice;
      case 'quarterly':
        return selectedPlan.quarterlyPrice;
      case 'yearly':
        return selectedPlan.yearlyPrice || Math.round(selectedPlan.monthlyPrice * 12 * 0.8); // 20% discount if not specified
      default:
        return selectedPlan.monthlyPrice;
    }
  };

  const formatCardNumber = (input: string) => {
    const cleaned = input.replace(/\D/g, '');
    const formatted = cleaned.replace(/(\d{4})(?=\d)/g, '$1 ');
    return formatted.substring(0, 19); // 16 digits + 3 spaces
  };

  const formatExpiryDate = (input: string) => {
    const cleaned = input.replace(/\D/g, '');
    if (cleaned.length > 2) {
      return `${cleaned.substring(0, 2)}/${cleaned.substring(2, 4)}`;
    }
    return cleaned;
  };

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCardNumber(formatCardNumber(e.target.value));
  };

  const handleExpiryDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setExpiryDate(formatExpiryDate(e.target.value));
  };

  const handleCvvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const cleaned = e.target.value.replace(/\D/g, '');
    setCvv(cleaned.substring(0, 3));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!cardNumber || !expiryDate || !cvv || !name) {
      setError('Please fill in all fields');
      return;
    }

    if (cardNumber.replace(/\s/g, '').length !== 16) {
      setError('Please enter a valid 16-digit card number');
      return;
    }

    if (expiryDate.length !== 5) {
      setError('Please enter a valid expiry date (MM/YY)');
      return;
    }

    if (cvv.length !== 3) {
      setError('Please enter a valid 3-digit CVV code');
      return;
    }

    try {
      const paymentMethodId = 'pm_card_visa';
      
      const price = getPrice();
      
      const result = await processPayment(paymentMethodId, {
        amount: price * 100, // Convert to cents for Stripe
        currency: 'usd',
        description: `${selectedPlan.name} Plan (${selectedPlanType})`,
        planId: selectedPlan.id,
        isSubscription: true,
        billingPeriod: selectedPlanType as 'monthly' | 'quarterly' | 'yearly',
        metadata: {
          planName: selectedPlan.name,
          planType: selectedPlanType,
          cardholderName: name
        }
      });
      
      if (!result.success) {
        throw new Error('Payment processing failed');
      }
      
      await onProcessPayment({
        paymentMethodId,
        cardholderName: name,
        planId: selectedPlan.id,
        planType: selectedPlanType,
        customerId: result.customerId,
        subscriptionId: result.subscriptionId
      });
    } catch (err) {
      setError('Payment processing failed. Please try again.');
    }
  };

  if (!selectedPlan) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="pt-6">
          <div className="flex items-center text-red-500 mb-4">
            <AlertTriangle className="h-5 w-5 mr-2" />
            <p>No plan selected. Please go back and select a plan.</p>
          </div>
          <Button variant="outline" onClick={onBackToPlanSelection}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Plan Selection
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <Button 
        variant="ghost" 
        className="mb-6" 
        onClick={onBackToPlanSelection}
        disabled={isProcessing}
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Plan Selection
      </Button>

      <Card className="border-none shadow-lg bg-gradient-to-br from-white to-blue-50">
        <CardHeader>
          <CardTitle className="text-xl flex items-center">
            <CreditCard className="h-5 w-5 mr-2 text-primary" />
            Complete Your Subscription
          </CardTitle>
          <CardDescription>
            Enter your payment details to activate the {selectedPlan.name} plan
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="md:col-span-2">
              <h3 className="text-lg font-medium mb-2">Payment Details</h3>
              <PaymentForm
                cardNumber={cardNumber}
                expiryDate={expiryDate}
                cvv={cvv}
                name={name}
                error={error}
                isProcessing={isProcessing}
                onCardNumberChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                onExpiryDateChange={(e) => setExpiryDate(formatExpiryDate(e.target.value))}
                onCvvChange={(e) => setCvv(e.target.value.replace(/\D/g, '').substring(0, 3))}
                onNameChange={(e) => setName(e.target.value)}
              />
            </div>
            <OrderSummary
              selectedPlan={selectedPlan}
              selectedPlanType={selectedPlanType}
              getPrice={getPrice}
            />
          </div>
        </CardContent>
        <CardFooter className="flex justify-end">
          <Button 
            type="submit"
            disabled={isProcessing}
            className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white"
            onClick={handleSubmit}
          >
            {isProcessing ? (
              <span className="flex items-center gap-2">
                <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full"></span>
                Processing...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Complete Payment
              </span>
            )}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default SubscriptionCheckout;
